import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import QuestionForm from '../../components/questions/QuestionForm';

const QuestionEditPage = () => {
  const { courseId, questionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch question details');
        }
        
        const data = await response.json();
        setQuestion(data);
        
        // Check if user is authorized to edit
        if (user.id !== data.userId && user.role !== 'admin' && user.role !== 'moderator') {
          navigate(`/courses/${courseId}/questions/${questionId}`);
          toast.error('You are not authorized to edit this question');
        }
      } catch (error) {
        console.error('Error fetching question details:', error);
        setError(error.message);
        toast.error('Failed to load question details');
      } finally {
        setLoading(false);
      }
    };
    
    if (questionId) {
      fetchQuestionDetails();
    }
  }, [questionId, user, navigate, courseId]);
  
  const handleSubmit = async (questionData) => {
    try {
      setSaving(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(questionData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update question');
      }
      
      toast.success('Question updated successfully');
      navigate(`/courses/${courseId}/questions/${questionId}`);
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-6 h-6 border-t-2 border-b-2 border-red-500 rounded-full animate-spin mr-2"></div>
        <span className="text-gray-600 dark:text-gray-400">Loading question...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Question
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(`/courses/${courseId}/questions/${questionId}`)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Question
        </button>
      </div>
    );
  }
  
  if (!question) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Question Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The question you're trying to edit couldn't be found or you don't have access to it.
        </p>
        <Link
          to={`/courses/${courseId}?tab=questions`}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Questions
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link 
          to={`/courses/${courseId}`} 
          className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          {question.courseCode}: {question.courseName}
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link 
          to={`/courses/${courseId}?tab=questions`} 
          className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          Questions
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <Link 
          to={`/courses/${courseId}/questions/${questionId}`} 
          className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
        >
          {question.title?.substring(0, 30)}{question.title?.length > 30 ? '...' : ''}
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          Edit
        </span>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Edit Question
          </h1>
          
          <Link
            to={`/courses/${courseId}/questions/${questionId}`}
            className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <ArrowLeft className="mr-1 -ml-1 h-4 w-4" />
            Cancel
          </Link>
        </div>
        
        <QuestionForm
          initialData={{
            id: question.id,
            title: question.title,
            content: question.content
          }}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/courses/${courseId}/questions/${questionId}`)}
        />
      </div>
    </div>
  );
};

export default QuestionEditPage;